/**
 * Event capture + user identification helpers.
 *
 * Every function here is a clean no-op until `initObservability` has run with a
 * valid PostHog key. This is the safe-by-default contract: call sites never
 * have to guard on "is analytics enabled?".
 *
 * BROWSER-SAFE.
 */
import { buildSentryUser } from './sentry-user.js';
import { getPosthogClient, isSentryReady } from './state.js';
/**
 * Capture a product event on the value loop. Forwards to PostHog when
 * initialised; no-op otherwise.
 *
 * @param name  A canonical funnel event (type-checked + autocompleted) or any
 *              product-specific string.
 * @param props Optional event properties.
 */
export function captureEvent(name, props) {
    const client = getPosthogClient();
    if (!client)
        return;
    client.capture(name, props);
}
/** Apply a same-session consent change to the initialized PostHog client. */
export function setAnalyticsConsent(granted) {
    const client = getPosthogClient();
    if (!client)
        return false;
    if (granted)
        client.opt_in_capturing();
    else
        client.opt_out_capturing();
    return true;
}
/**
 * Identify the current user across PostHog + Sentry. Uses the platform UUID as
 * the identifier — NEVER email (email is a purgeable property). No-op until
 * the respective subsystem is initialised.
 */
export function identifyUser(user) {
    const client = getPosthogClient();
    if (client) {
        client.identify(user.id, {
            email: user.email,
            username: user.username,
            role: user.role,
            vessel_id: user.vesselId,
            vessel_name: user.vesselName,
        });
    }
    if (isSentryReady()) {
        // Lazy require avoids paying for Sentry symbols when it never initialised.
        void applySentryUser(user);
    }
}
/**
 * Clear the identified user on logout (GDPR-friendly reset). No-op until
 * initialised.
 */
export function resetUser() {
    const client = getPosthogClient();
    if (client) {
        client.reset();
    }
    if (isSentryReady()) {
        void clearSentryUser();
    }
}
async function applySentryUser(user) {
    const Sentry = await import('@sentry/react');
    Sentry.setUser(buildSentryUser(user));
    if (user.vesselId)
        Sentry.setTag('vessel_id', user.vesselId);
    if (user.vesselName)
        Sentry.setTag('vessel_name', user.vesselName);
}
async function clearSentryUser() {
    const Sentry = await import('@sentry/react');
    Sentry.setUser(null);
    Sentry.setTag('vessel_id', undefined);
    Sentry.setTag('vessel_name', undefined);
}
